import type { ReactNode } from 'react';

export function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h3 className="text-base font-semibold">{title}</h3>
      {description && <p className="muted mt-0.5 text-sm">{description}</p>}
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

/** A bare on/off switch (Toggle without the label row). */
export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition ${checked ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} />
    </button>
  );
}

export function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4">
      <span>{label}</span>
      <Switch checked={checked} onChange={onChange} />
    </label>
  );
}

/**
 * The top of each widget's settings page: its on/off switch, then its options (only while
 * it is on, so a switched-off widget's page stays short).
 */
export function WidgetPage({
  label,
  description,
  enabled,
  onChange,
  children,
}: {
  label: string;
  description?: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <>
      <div className="mb-8 rounded-xl border border-[var(--border)] p-4">
        <Toggle label={label} checked={enabled} onChange={onChange} />
        {description && <p className="muted mt-1 text-sm">{description}</p>}
      </div>
      {enabled ? children : <p className="muted text-sm">Switch it on to see its options.</p>}
    </>
  );
}

export function Choice<T extends string>({
  options,
  value,
  onChange,
  columns = 2,
}: {
  options: Array<{ id: T; name: string; description?: string }>;
  value: T;
  onChange: (v: T) => void;
  columns?: number;
}) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }} role="radiogroup">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={o.id === value}
          onClick={() => onChange(o.id)}
          className={`rounded-xl border p-3 text-left transition ${
            o.id === value ? 'border-[var(--accent)] bg-[var(--accent)]/10' : 'border-[var(--border)] hover:border-[var(--fg-muted)]'
          }`}
        >
          <div className="text-sm font-medium">{o.name}</div>
          {o.description && <div className="muted mt-0.5 text-xs">{o.description}</div>}
        </button>
      ))}
    </div>
  );
}

export const inputClass =
  'w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]';

export const buttonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-lg bg-[var(--accent)] px-3 py-2 text-sm font-medium text-black transition hover:opacity-90 disabled:opacity-50';

export const ghostButtonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-2 text-sm transition hover:border-[var(--fg-muted)] disabled:opacity-50';
